import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FiCalendar, FiPlus, FiEdit2, FiTrash2, FiExternalLink } from "react-icons/fi";
import { Button, Card, EmptyState, Input, Modal, Spinner, Textarea } from "../ui";
import { getEvents, createEvent, updateEvent, deleteEvent } from "../../api/communityContent";
import { useToast } from "../../context/ToastContext";
import { useConfirm } from "../../context/ConfirmContext";

export default function EventsTab({ communityId, isAdmin }) {
  const [searchParams] = useSearchParams();
  const highlightEventId = searchParams.get("event");
  const highlightRef = useRef(null);
  const { notify } = useToast();
  const confirm = useConfirm();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventModal, setEventModal] = useState(null);
  const [eventForm, setEventForm] = useState({ title: "", description: "", date: "", location: "", registrationLink: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    getEvents(communityId)
      .then((res) => setEvents(res.data?.events || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [communityId]);

  useEffect(() => {
    if (!loading && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [loading, highlightEventId]);

  const openModal = (ev) => {
    setEventModal(ev || "new");
    setEventForm(
      ev
        ? { title: ev.title, description: ev.description || "", date: ev.date ? ev.date.slice(0, 10) : "", location: ev.location || "", registrationLink: ev.registrationLink || "" }
        : { title: "", description: "", date: "", location: "", registrationLink: "" }
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      if (eventModal === "new") {
        const r = await createEvent(communityId, eventForm);
        setEvents((prev) => [...prev, r.data.event]);
      } else {
        const r = await updateEvent(communityId, eventModal.id, eventForm);
        setEvents((prev) => prev.map((e) => (e.id === eventModal.id ? r.data.event : e)));
      }
      setEventModal(null);
      notify("Event saved.", "success");
    } catch {
      notify("Couldn't save event.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (evId) => {
    const ok = await confirm({ title: "Delete this event?", description: "This event will be permanently removed for everyone in the community. This cannot be undone." });
    if (!ok) return;
    try {
      await deleteEvent(communityId, evId);
      setEvents((prev) => prev.filter((e) => e.id !== evId));
      notify("Event deleted.", "success");
    } catch {
      notify("Couldn't delete event.", "error");
    }
  };

  if (loading) {
    return (
      <Card className="flex justify-center py-12">
        <Spinner />
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {isAdmin && (
        <div className="flex justify-end">
          <Button onClick={() => openModal(null)}>
            <FiPlus className="h-4 w-4" /> Add event
          </Button>
        </div>
      )}
      {events.length === 0 ? (
        <EmptyState
          icon={<FiCalendar className="h-6 w-6" />}
          title="No events yet"
          description={isAdmin ? "Create the first event for this community." : "Check back soon for upcoming events."}
          action={isAdmin && <Button onClick={() => openModal(null)}><FiPlus className="h-4 w-4" /> Add event</Button>}
        />
      ) : (
        events.map((ev) => {
          const isHighlighted = String(ev.id) === highlightEventId;
          return (
            <Card key={ev.id} ref={isHighlighted ? highlightRef : undefined} className={`p-4 ${isHighlighted ? "ring-2 ring-primary-400" : ""}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-slate-900">{ev.title}</p>
                  {ev.date && (
                    <p className="mt-1 text-sm font-medium text-primary-600">
                      {new Date(ev.date).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                    </p>
                  )}
                  {ev.location && <p className="mt-0.5 text-sm text-slate-500">📍 {ev.location}</p>}
                  {ev.description && <p className="mt-1 text-sm text-slate-500">{ev.description}</p>}
                  {ev.registrationLink && (
                    <a
                      href={ev.registrationLink}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline"
                    >
                      Register Here <FiExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
                {isAdmin && (
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => openModal(ev)} className="text-slate-400 hover:text-primary-600">
                      <FiEdit2 className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(ev.id)} className="text-slate-400 hover:text-red-500">
                      <FiTrash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </Card>
          );
        })
      )}

      <Modal
        open={Boolean(eventModal)}
        onClose={() => setEventModal(null)}
        title={eventModal === "new" ? "Add event" : "Edit event"}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEventModal(null)}>Cancel</Button>
            <Button onClick={save} loading={saving}>Save event</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input label="Event title" value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} />
          <Input label="Date" type="date" value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} />
          <Input label="Location" value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} />
          <Input label="Registration link (optional)" placeholder="https://…" value={eventForm.registrationLink} onChange={(e) => setEventForm({ ...eventForm, registrationLink: e.target.value })} />
          <Textarea label="Description" rows={3} value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} />
        </div>
      </Modal>
    </div>
  );
}
